import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "antd";
import {
  ButtonPair,
  ButtonRow,
  ButtonStack,
  DecidedBanner,
  DecisionBarWrapper,
  ErrorBanner,
  NoteHint,
  NoteInput,
  NoteLabel,
  QueueActions,
  RevealDetail,
  RevealHeadline,
  RevealPanel,
  SecondaryButton,
  SignUpButton,
  TextInput,
  TurnDownButton,
} from "../IntakeReviewStyled";
import {
  actOnIntakeReview,
  clearIntakeReviewActionError,
  closeIntakeReviewCard,
  dismissExplanationRequest,
} from "../../../slice/IntakeReviewSlice";
import { secondsOnCard } from "../utils";

// The decisions-inside-the-decision from Litify's card, each of which
// starts its own follow-up work when a case is signed up.
// How each outcome reads in a sentence. The system only ever guesses the
// first two; a refer-out is a third thing it cannot have predicted.
const OUTCOME_WORDS = {
  signed_up: "sign up",
  turned_down: "turn down",
  referred_out: "refer out",
};

const SPECIAL_SIGN_UPS = [
  "Proposed Reduction",
  "Deferred Conditions",
  "AFFF",
  "Suboxone",
  "Roundup",
  "SSDI sign-up",
];

/**
 * The four outcomes plus the queue moves.
 *
 * The one hard gate is that an intake cannot be turned down or referred
 * out without the reviewer's own words. Litify had the field and never
 * wrote it, so none of last year's 2,182 VA turn-downs has a recorded
 * reason and none of them can teach the system anything.
 *
 * The reasoning box is ALWAYS on screen and names both outcomes
 * (Matthew, 2026-09-17: "I want to see the 'why did you sign this one
 * up' text entry always"). It stays optional for a sign-up — 72% of
 * decisions are sign-ups and blocking each one on prose would make the
 * queue unworkable — but it is visibly an invitation rather than a box
 * that only appears when something is wrong. Positive examples are the
 * half of the training set Litify never collected at all, and the
 * reviewer is the right judge of when a sign-up is worth a line.
 *
 * The post-decision prompt is now only a backstop: if a surprising
 * sign-up was recorded with nothing typed, it asks once. It fires
 * *after* the decision on purpose — telling the reviewer beforehand that
 * his expected answer is the unexpected one would nudge him, and nudged
 * decisions are worth nothing as evidence.
 */
const DecisionBar = () => {
  const dispatch = useDispatch();
  const noteRef = useRef(null);
  const {
    card,
    acting,
    actionError,
    actionErrorField,
    sectionsOpened,
    openedAt,
    explanationWanted,
    lastDecision,
    lastNote,
    reveal,
  } = useSelector((state) => state.intakeReviews);

  const [note, setNote] = useState("");
  const [firm, setFirm] = useState("");
  const [question, setQuestion] = useState("");
  // Why the last click was refused before it reached the server.
  const [hint, setHint] = useState("");
  const [armed, setArmed] = useState(null); // null | "ask" | "referOut" | "assign"
  const [assignee, setAssignee] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [explanation, setExplanation] = useState("");

  const reviewers = useMemo(() => card?.reviewers || [], [card]);
  // The firms a refer-out may go to come from the referrals portal's
  // directory (Matthew, 2026-10-31: refer out from the review; the
  // attorney never touches Litify). Until firms are set up the box stays
  // free text and the referral is recorded here only.
  const referralFirms = useMemo(() => card?.referralFirms || [], [card]);

  if (!card) return null;

  const trimmedNote = note.trim();

  const send = (payload) =>
    dispatch(
      actOnIntakeReview({
        id: card.id,
        sectionsOpened,
        secondsOnCard: secondsOnCard(openedAt),
        ...payload,
      })
    );

  const demandNote = (message) => {
    dispatch(clearIntakeReviewActionError());
    setHint(message);
    noteRef.current?.focus();
  };

  const onNoteChange = (event) => {
    setNote(event.target.value);
    if (hint) setHint("");
  };

  const onSignUp = () =>
    send({
      action: "signUp",
      note: trimmedNote,
      specialSignUps: specials,
    });

  const onTurnDown = () => {
    if (!trimmedNote) {
      return demandNote("Write why you are turning this down first.");
    }
    return send({ action: "turnDown", note: trimmedNote });
  };

  const onSendQuestion = () => {
    const text = question.trim();
    if (!text) return null;
    return send({ action: "askQuestion", note: text });
  };

  // A refer-out needs no reason (Matthew, 2026-10-31: "It'll be friction
  // for now, we can add it later if we want"); a turn-down still does.
  const onReferOut = () => {
    if (!firm.trim()) return null;
    return send({
      action: "referOut",
      note: trimmedNote,
      referredTo: firm.trim(),
    });
  };

  const onExplain = () => {
    if (!explanation.trim()) return null;
    return send({ action: "explain", note: explanation.trim() }).then(() => {
      setExplanation("");
      dispatch(dismissExplanationRequest());
    });
  };

  // After the decision: reveal the system's guess, and — only where the
  // two differ — ask for the sentence. The decision is already recorded,
  // so none of this can change it; the sentence is what lets the firm
  // learn why a reviewer went the other way.
  if (explanationWanted || reveal) {
    const decided = OUTCOME_WORDS[lastDecision] || "decide";
    const guessed = reveal ? OUTCOME_WORDS[reveal.outcome] : null;
    const differs = Boolean(reveal) && reveal.agreed === false;
    // A reviewer who already wrote a reason (always so for a turn-down)
    // has explained himself; the box is only for when he has not.
    const showBox = explanationWanted;

    return (
      <DecisionBarWrapper>
        <DecidedBanner>Recorded: you chose to {decided}.</DecidedBanner>

        {reveal ? (
          <RevealPanel $differs={differs} role="status">
            <RevealHeadline $differs={differs}>
              {differs
                ? `The system guessed ${guessed} — you went the other way.`
                : `The system guessed ${guessed} too — you agree.`}
            </RevealHeadline>
            {reveal.rationale ? (
              <RevealDetail>{reveal.rationale}</RevealDetail>
            ) : null}
            {differs && !showBox && lastNote ? (
              <RevealDetail>Your reason: “{lastNote}”</RevealDetail>
            ) : null}
          </RevealPanel>
        ) : null}

        {showBox ? (
          <>
            <NoteLabel htmlFor="intake-review-explanation">
              {reveal
                ? "Why did you go a different way than the system?"
                : "Anything worth recording about why you took this one?"}
            </NoteLabel>
            <NoteHint>
              One sentence, in your own words. This is how the system learns
              from the cases it gets wrong, so it is worth the thirty seconds.
            </NoteHint>
            <NoteInput
              id="intake-review-explanation"
              value={explanation}
              onChange={(event) => setExplanation(event.target.value)}
              placeholder="e.g. Rating looks maxed but the knee grant is clearly understated on the 2023 letter."
            />
            <ButtonRow>
              <SignUpButton
                type="button"
                onClick={onExplain}
                disabled={acting || !explanation.trim()}
              >
                Save and move on
              </SignUpButton>
              <SecondaryButton
                type="button"
                onClick={() => dispatch(dismissExplanationRequest())}
                disabled={acting}
              >
                Skip
              </SecondaryButton>
            </ButtonRow>
          </>
        ) : (
          <ButtonRow>
            <SignUpButton
              type="button"
              onClick={() => dispatch(closeIntakeReviewCard())}
            >
              Next case
            </SignUpButton>
          </ButtonRow>
        )}
      </DecisionBarWrapper>
    );
  }

  if (!card.canDecide) {
    return (
      <DecisionBarWrapper>
        <DecidedBanner>
          {card.statusLabel}
          {card.decidedBy ? ` by ${card.decidedBy.name}` : ""}
          {card.decisionNote ? ` — "${card.decisionNote}"` : ""}
        </DecidedBanner>
      </DecisionBarWrapper>
    );
  }

  if (armed === "ask") {
    return (
      <DecisionBarWrapper>
        {actionError ? (
          <ErrorBanner role="alert">{actionError}</ErrorBanner>
        ) : null}
        <NoteLabel htmlFor="intake-review-question">
          What should intake find out?
        </NoteLabel>
        <NoteHint>
          Sent to the intake team exactly as written. The intake goes to
          Post-TC Info Request with a chase task, and comes back here once
          they answer.
        </NoteHint>
        <NoteInput
          id="intake-review-question"
          autoFocus
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          $invalid={actionErrorField === "note"}
          placeholder="e.g. Did he ever file for the knee, or only the back? Please get the 2023 decision letter."
        />
        <ButtonStack>
          <SignUpButton
            type="button"
            onClick={onSendQuestion}
            disabled={acting || !question.trim()}
          >
            Send question to intake
          </SignUpButton>
          <SecondaryButton
            type="button"
            onClick={() => setArmed(null)}
            disabled={acting}
          >
            Cancel
          </SecondaryButton>
        </ButtonStack>
      </DecisionBarWrapper>
    );
  }

  return (
    <DecisionBarWrapper>
      {actionError ? (
        <ErrorBanner role="alert">{actionError}</ErrorBanner>
      ) : hint ? (
        <ErrorBanner role="alert">{hint}</ErrorBanner>
      ) : null}

      <NoteLabel htmlFor="intake-review-note">
        Why are you signing this up, or turning it down?
      </NoteLabel>
      <NoteHint>
        Required to turn down. Optional on a sign-up or a refer-out, but
        always worth a line — in your own words, not a category.
      </NoteHint>
      <NoteInput
        id="intake-review-note"
        ref={noteRef}
        value={note}
        onChange={onNoteChange}
        $invalid={actionErrorField === "note"}
        placeholder="e.g. Clear nexus and the 2023 letter understates the knee. Or: already 100% permanent and total, nothing left to win."
      />

      {armed === "referOut" ? (
        <div style={{ marginTop: 12 }}>
          <NoteLabel htmlFor="intake-review-firm">
            Which firm is it going to?
          </NoteLabel>
          {referralFirms.length > 0 ? (
            <>
              <Select
                id="intake-review-firm"
                style={{ width: "100%" }}
                placeholder="Choose a firm"
                value={firm || undefined}
                onChange={(value) => setFirm(value || "")}
                status={actionErrorField === "referredTo" ? "error" : undefined}
                options={referralFirms.map((f) => ({
                  value: f.key,
                  label:
                    f.states && f.states.length
                      ? `${f.name} (${f.states.join(", ")})`
                      : f.name,
                }))}
                showSearch
                optionFilterProp="label"
              />
              <NoteHint>
                The firm is notified through the referrals portal within five
                minutes and sees the case without the client&apos;s name until
                it signs up. The intake resolves as Referred Out and a Referral
                record carries it from here.
              </NoteHint>
            </>
          ) : (
            <TextInput
              id="intake-review-firm"
              value={firm}
              onChange={(event) => setFirm(event.target.value)}
              $invalid={actionErrorField === "referredTo"}
              placeholder="e.g. Skye Law"
            />
          )}
        </div>
      ) : null}

      {armed === "assign" ? (
        <div style={{ marginTop: 12 }}>
          <NoteLabel htmlFor="intake-review-assignee">
            Hand it to which reviewer?
          </NoteLabel>
          <Select
            id="intake-review-assignee"
            style={{ width: "100%" }}
            placeholder="Choose a reviewer"
            value={assignee}
            onChange={setAssignee}
            options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
            notFoundContent="No other reviewers available"
          />
        </div>
      ) : null}

      {armed === null ? (
        <div style={{ marginTop: 12 }}>
          <NoteLabel htmlFor="intake-review-specials">
            Anything special about this sign-up?
          </NoteLabel>
          <Select
            id="intake-review-specials"
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Usually nothing"
            value={specials}
            onChange={setSpecials}
            options={SPECIAL_SIGN_UPS.map((s) => ({ value: s, label: s }))}
          />
        </div>
      ) : null}

      <ButtonRow>
        {armed === "referOut" ? (
          <>
            <SignUpButton
              type="button"
              onClick={onReferOut}
              disabled={acting || !firm.trim()}
            >
              Confirm referral
            </SignUpButton>
            <SecondaryButton
              type="button"
              onClick={() => setArmed(null)}
              disabled={acting}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : armed === "assign" ? (
          <>
            <SignUpButton
              type="button"
              onClick={() =>
                send({ action: "assign", assigneeId: assignee }).then(() =>
                  setArmed(null)
                )
              }
              disabled={acting || !assignee}
            >
              Hand it over
            </SignUpButton>
            <SecondaryButton
              type="button"
              onClick={() => setArmed(null)}
              disabled={acting}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : null}
      </ButtonRow>

      {armed === null ? (
        <>
          <ButtonStack>
            <SignUpButton type="button" onClick={onSignUp} disabled={acting}>
              Sign up
            </SignUpButton>
            <TurnDownButton
              type="button"
              onClick={onTurnDown}
              disabled={acting}
            >
              Turn down
            </TurnDownButton>
            <ButtonPair>
              <SecondaryButton
                type="button"
                onClick={() => setArmed("ask")}
                disabled={acting}
              >
                Ask intake
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => setArmed("referOut")}
                disabled={acting}
              >
                Refer out
              </SecondaryButton>
            </ButtonPair>
          </ButtonStack>

          <QueueActions>
            <SecondaryButton
              type="button"
              onClick={() => setArmed("assign")}
              disabled={acting}
            >
              Hand over
            </SecondaryButton>
          </QueueActions>
        </>
      ) : null}
    </DecisionBarWrapper>
  );
};

export default DecisionBar;
